/**
 * Renders a follow button that enables management of both followers and following users.
 * The follow button options change based on the "owner" of the button (i.e. the user who is a follower or following).
 * The renderable action of the button(s) has the following rules:
 *  1. If the owner is in following, check follow status, then show "Followed" or a "Requested" button
 *  2. If the owner is in followers,
 *      If you are a private user and the owner has requested to follow, show a confirm/delete button
 *      If the owner is in following, then show a "Followed" or "Requested" button
 *      If the owner is not in following, then show a "Follow back"
 *  3. If pageOwner is in neither, then show a "Follow" button
 *
 * Copyright (c) 2019 Propellant Software Corp
 * Author: Kelvin Nguyen
 */

import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Button, Modal, ModalHeader, ModalBody } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FOLLOWS, FOLLOW_REQUEST } from 'client/shared/js/enums/relationships/follows'
import { forceAuthRedirect } from 'client/shared/js/services/auth/api'
import ProfilePhoto from 'client/shared/js/components/ProfilePhoto'
import LoadButton from 'client/shared/js/components/LoadButton'
import { isSameUser, getUserFollowing, getUserFollower } from '../../../utils/accessInterpreter/user'

const FOLLOWING = 'following'
const FOLLOWERS = 'followers'

export default class UserFollowButton extends Component {
  static propTypes = {
    user: PropTypes.object,
    owner: PropTypes.object.isRequired,
    status: PropTypes.object,
    type: PropTypes.string.isRequired,
    onFollowUser: PropTypes.func,
    onUnfollowUser: PropTypes.func,
    onApproveFollowRequest: PropTypes.func,
    onDeleteFollow: PropTypes.func
  }

  static defaultProps = {
    status: {},
    type: FOLLOWING // followers or following
  }

  state = {
    isRemoveFollowerModal: false
  }

  isLoading = () => {
    const followRequest = this.props.status.followRequest
    return !!(followRequest && followRequest[this.props.owner.uid])
  }

  handleFollowUser = () => {
    const uid = this.props.user.uid
    const influencerId = this.props.owner.uid

    this.props.onFollowUser(uid, influencerId)
  }

  handleUnfollowUser = () => {
    const uid = this.props.user.uid
    const influencerId = this.props.owner.uid

    this.props.onUnfollowUser(uid, influencerId)
  }

  handleApproveFollowRequest = () => {
    const uid = this.props.user.uid
    const requestUid = this.props.owner.uid

    this.props.onApproveFollowRequest(uid, requestUid)
  }

  handleDeleteFollow = (type) => {
    const uid = this.props.user.uid
    const requestUid = this.props.owner.uid

    this.props.onDeleteFollow(uid, requestUid, type)
  }

  toggleRemoveFollowerModal = () => {
    this.setState({
      isRemoveFollowerModal: !this.state.isRemoveFollowerModal
    })
  }

  renderRemoveFollowerModal = () => {
    const { photoUrl, username } = this.props.owner

    return (this.state.isRemoveFollowerModal) ? (
      <Modal className='remove-follower-modal' isOpen={this.state.isRemoveFollowerModal} centered>
        <ModalHeader className='align-items-center'>
          Remove follower?
        </ModalHeader>
        <ModalBody>
          <div className='d-flex justify-content-center align-items-center pb-2'>
            <ProfilePhoto photoUrl={photoUrl} size='84px' />
            <p className='ml-4 mb-0'>HipHerd won't tell <b>@{username}</b> they were removed from your followers</p>
          </div>
          <div className='d-flex justify-content-between mt-4'>
            <Button onClick={this.toggleRemoveFollowerModal} color='light'>
              Cancel
            </Button>
            <LoadButton
              isLoading={this.isLoading()}
              color='danger'
              onClick={() => this.handleDeleteFollow(FOLLOWS)}>
              Remove
            </LoadButton>
          </div>
        </ModalBody>
      </Modal>
    ) : null
  }

  renderDefaultFollowButton = (btnText, onClick) => {
    return (
      <LoadButton
        className='follow-btn'
        color='primary'
        isLoading={this.isLoading()}
        onClick={onClick}>
        {btnText}
      </LoadButton>
    )
  }

  renderRemoveFollowerButton = () => {
    return (this.props.onDeleteFollow) ? (
      <>
        <Button onClick={this.toggleRemoveFollowerModal} color='danger' size='sm' className='follow-btn round'>
          <FontAwesomeIcon icon={['fal', 'circle-minus']} />
        </Button>
        {this.renderRemoveFollowerModal()}
      </>
    ) : null
  }

  renderFollowersButton = (userFollower, userFollowing) => {
    const type = userFollower.type

    if (type === FOLLOW_REQUEST) {
      return (
        <>
          {this.renderDefaultFollowButton('Confirm', this.handleApproveFollowRequest)}
          <LoadButton
            className='follow-btn'
            isLoading={this.isLoading()}
            color='light'
            onClick={() => this.handleDeleteFollow(FOLLOW_REQUEST)}>
            Deny
          </LoadButton>
        </>
      )
    } else if (type === FOLLOWS) {
      if (userFollowing) {
        return this.renderFollowingButton(userFollowing)
      }

      return (
        <>
          {this.renderDefaultFollowButton('Follow back', this.handleFollowUser)}
          {this.renderRemoveFollowerButton()}
        </>
      )
    }
  }

  renderFollowingButton = (userFollowing) => {
    const type = userFollowing.type
    let followTxt = 'Follow'

    if (type === FOLLOW_REQUEST) {
      followTxt = 'Requested'
    } else if (type === FOLLOWS) {
      followTxt = 'Unfollow'
    }

    return (
      <>
        <LoadButton
          className='follow-btn'
          isLoading={this.isLoading()}
          color='light'
          onClick={this.handleUnfollowUser}>
          {followTxt}
        </LoadButton>
        {this.renderRemoveFollowerButton()}
      </>
    )
  }

  render () {
    if (!isSameUser(this.props.user, this.props.owner)) {
      // If a user is signed in, show the appropriate follow buttons
      if (this.props.user.uid) {
        const followUid = this.props.owner.uid
        const userFollowing = getUserFollowing(this.props.user, followUid)
        const userFollower = getUserFollower(this.props.user, followUid)

        if (this.props.type === FOLLOWING && userFollowing) {
          return this.renderFollowingButton(userFollowing)
        } else if (this.props.type === FOLLOWERS && userFollower) {
          return this.renderFollowersButton(userFollower, userFollowing)
        }
      } else {
        return this.renderDefaultFollowButton('Follow', () => forceAuthRedirect(null, false))
      }

      // If you are not a follower of the owner and if the owner is not currently following you
      return this.renderDefaultFollowButton('Follow', this.handleFollowUser)
    }

    return null
  }
}
